import { useState } from 'react';
import { useLang } from '../contexts/LanguageContext';
import { useScrollReveal, revealClass } from '../hooks/useScrollReveal';
import { db } from '../firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { Mail, MapPin, Clock, Send, Phone } from 'lucide-react';

export default function Contact() {
  const { t } = useLang();
  const { ref, isVisible } = useScrollReveal();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service) return;
    setStatus('submitting');
    try {
      // 1. Save lead to Firestore (CRM)
      await addDoc(collection(db, 'leads'), {
        ...form,
        timestamp: serverTimestamp(),
        status: 'new',
      });

      // 2. Trigger email via Firebase Extension (writes to 'mail' collection)
      await addDoc(collection(db, 'mail'), {
        to: 'geral@rjmontagens.pt',
        message: {
          subject: `Novo Pedido de Orçamento — ${form.name}`,
          html: `
            <div style="font-family:'Segoe UI',sans-serif;max-width:560px;margin:0 auto;background:#fff;border-radius:12px;overflow:hidden;border:1px solid #e4e4e7">
              <div style="background:#C2410C;padding:24px 28px">
                <h1 style="color:#fff;margin:0;font-size:20px;font-weight:700">RJ Montagens — Novo Pedido</h1>
                <p style="color:#fcd5c0;margin:4px 0 0;font-size:14px">Recebido via website</p>
              </div>
              <div style="padding:28px">
                <table style="width:100%;border-collapse:collapse">
                  <tr><td style="padding:10px 0;border-bottom:1px solid #f4f4f5;color:#71717a;font-size:13px;width:110px;vertical-align:top">Nome</td><td style="padding:10px 0;border-bottom:1px solid #f4f4f5;font-weight:600;color:#09090b">${form.name}</td></tr>
                  <tr><td style="padding:10px 0;border-bottom:1px solid #f4f4f5;color:#71717a;font-size:13px;vertical-align:top">Email</td><td style="padding:10px 0;border-bottom:1px solid #f4f4f5;color:#09090b"><a href="mailto:${form.email}" style="color:#C2410C">${form.email}</a></td></tr>
                  <tr><td style="padding:10px 0;border-bottom:1px solid #f4f4f5;color:#71717a;font-size:13px;vertical-align:top">Telefone</td><td style="padding:10px 0;border-bottom:1px solid #f4f4f5;color:#09090b">${form.phone || '—'}</td></tr>
                  <tr><td style="padding:10px 0;border-bottom:1px solid #f4f4f5;color:#71717a;font-size:13px;vertical-align:top">Serviço</td><td style="padding:10px 0;border-bottom:1px solid #f4f4f5;font-weight:600;color:#C2410C">${form.service}</td></tr>
                  <tr><td style="padding:10px 0;color:#71717a;font-size:13px;vertical-align:top">Mensagem</td><td style="padding:10px 0;color:#09090b;line-height:1.6">${form.message || '—'}</td></tr>
                </table>
                <div style="margin-top:24px;padding:16px;background:#fafafa;border-radius:8px;border-left:3px solid #C2410C">
                  <p style="margin:0;font-size:13px;color:#71717a">Para ver todos os leads: <a href="https://rjmontagens.pt/admin" style="color:#C2410C;font-weight:600">Painel de Administração</a></p>
                </div>
              </div>
            </div>
          `,
        },
      });

      setStatus('success');
      setForm({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  return (
    <section id="contacto" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={revealClass(isVisible)}>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#C2410C] mb-4">
            <span className="w-6 h-px bg-[#C2410C]" />
            {t('contact_label')}
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 tracking-tight mb-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="contact-title"
          >
            {t('contact_title')}
          </h2>
          <p className="text-base sm:text-lg text-zinc-500 max-w-xl mb-12">
            {t('contact_subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Form — 3 cols */}
          <form
            onSubmit={handleSubmit}
            className="lg:col-span-3 space-y-5"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                  {t('form_name')} <span className="text-[#C2410C]">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="João Silva"
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#C2410C]/30 focus:border-[#C2410C] text-sm text-zinc-900 placeholder:text-zinc-400 transition-all"
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                  {t('form_email')} <span className="text-[#C2410C]">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="joao@email.com"
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#C2410C]/30 focus:border-[#C2410C] text-sm text-zinc-900 placeholder:text-zinc-400 transition-all"
                  data-testid="contact-input-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                  {t('form_phone')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+351 912 345 678"
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#C2410C]/30 focus:border-[#C2410C] text-sm text-zinc-900 placeholder:text-zinc-400 transition-all"
                  data-testid="contact-input-phone"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                  {t('form_service')} <span className="text-[#C2410C]">*</span>
                </label>
                <select
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#C2410C]/30 focus:border-[#C2410C] text-sm text-zinc-900 bg-white transition-all"
                  data-testid="contact-select-service"
                >
                  <option value="">{t('form_service_placeholder')}</option>
                  {(t('service_options') || []).map((opt, i) => (
                    <option key={i} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                {t('form_message')}
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={5}
                placeholder={t('form_message') + '...'}
                className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#C2410C]/30 focus:border-[#C2410C] text-sm text-zinc-900 placeholder:text-zinc-400 transition-all resize-none"
                data-testid="contact-input-message"
              />
            </div>

            {status === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm font-medium" data-testid="contact-success">
                {t('form_success')}
              </div>
            )}
            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm font-medium" data-testid="contact-error">
                {t('form_error')}
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C2410C] hover:bg-[#9A3412] disabled:opacity-60 text-white font-semibold rounded-full transition-all duration-200 active:scale-95 shadow-sm text-base w-full sm:w-auto justify-center"
              data-testid="contact-submit-btn"
            >
              <Send size={18} />
              {status === 'submitting' ? t('form_submitting') : t('form_submit')}
            </button>
          </form>

          {/* Contact info — 2 cols */}
          <div className="lg:col-span-2 space-y-7">
            <div>
              <h3
                className="text-lg font-bold text-zinc-900 mb-5"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                {t('contact_info_title')}
              </h3>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C2410C]/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Mail className="text-[#C2410C]" size={18} />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-zinc-400 mb-0.5">
                      {t('contact_email_label')}
                    </div>
                    <a
                      href="mailto:geral@rjmontagens.pt"
                      className="text-sm font-medium text-zinc-900 hover:text-[#C2410C] transition-colors"
                      data-testid="contact-email-link"
                    >
                      geral@rjmontagens.pt
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C2410C]/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Phone className="text-[#C2410C]" size={18} />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-zinc-400 mb-0.5">
                      {t('contact_phone_label')}
                    </div>
                    <a
                      href="tel:+351915995141"
                      className="text-sm font-medium text-zinc-900 hover:text-[#C2410C] transition-colors"
                      data-testid="contact-phone-link"
                    >
                      {t('contact_phone')}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C2410C]/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin className="text-[#C2410C]" size={18} />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-zinc-400 mb-0.5">
                      {t('contact_areas')}
                    </div>
                    <p className="text-sm font-medium text-zinc-700">
                      {t('contact_areas_value')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C2410C]/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Clock className="text-[#C2410C]" size={18} />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-zinc-400 mb-0.5">
                      {t('contact_hours')}
                    </div>
                    <p className="text-sm font-medium text-zinc-700">{t('contact_hours_value')}</p>
                    <p className="text-sm font-medium text-zinc-700">{t('contact_hours_value2')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA card */}
            <div className="bg-[#09090B] rounded-2xl p-7 text-white">
              <h4 className="font-bold text-lg mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Resposta em 24h
              </h4>
              <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
                Todos os pedidos de orçamento são respondidos em menos de 24 horas.
              </p>
              <div className="flex items-center gap-2 text-[#C2410C] font-semibold text-sm">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                Disponível agora
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 overflow-hidden rounded-2xl border border-zinc-200 shadow-sm">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3113.4762811043506!2d-9.363578224385387!3d38.706872757923854!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xac4ec7c2b68ee2b1%3A0x175e3136f0535e39!2sRJ%20Montagens%20e%20Remodela%C3%A7%C3%B5es!5e0!3m2!1sen!2sbr!4v1779007318924!5m2!1sen!2sbr"
            width="100%"
            height="320"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="RJ Montagens e Remodelações — Localização no Google Maps"
          />
        </div>
      </div>
    </section>
  );
}
